// The sign-in card: Google, GitHub, or a code sent by email, and for a first
// visit the way in as a guest. It is the gate over the room before anyone is
// signed in, and the dialog a guest opens to keep their work under an account.

import type { AuthProvidersResponse } from "../core/protocol.ts";
import { sendCode, signInWith, signInWithCode, SignInRefusal } from "../api/auth.ts";
import { track } from "../api/events.ts";
import { PRIVACY_PATH, TERMS_PATH } from "../core/legal.ts";
import { h, svg } from "./dom.ts";
import { chooseLang, lang, localized, t } from "./lang.ts";
import { LANG_INFO, LANGS } from "../core/i18n.ts";
import { rich } from "./names.ts";

export interface SignInOptions {
  providers: AuthProvidersResponse;
  title: string;
  lead: string;
  /** Present on the gate: come in without an account. */
  onGuest?: () => Promise<void>;
  /** Called once the visitor is signed in with an account. */
  onDone(): void;
  /** What brought the card up, for the event log. */
  why?: "gate" | "menu" | "paywall" | "play" | "create" | "download" | "remix" | "other";
}

const GOOGLE = `<svg viewBox="0 0 20 20" aria-hidden="true"><path fill="#4285f4" d="M19.600 10.200c0-.700-.100-1.400-.200-2H10v3.800h5.400a4.600 4.600 0 0 1-2 3v2.500h3.200c1.900-1.700 3-4.300 3-7.300z"/><path fill="#34a853" d="M10 20c2.700 0 5-.900 6.600-2.400l-3.200-2.500c-.900.600-2 1-3.400 1-2.600 0-4.800-1.800-5.600-4.100H1.100v2.600A10 10 0 0 0 10 20z"/><path fill="#fbbc05" d="M4.400 11.900a6 6 0 0 1 0-3.800V5.500H1.100a10 10 0 0 0 0 9z"/><path fill="#ea4335" d="M10 4c1.500 0 2.800.5 3.800 1.500l2.900-2.800A10 10 0 0 0 1.100 5.500l3.300 2.600C5.200 5.700 7.400 4 10 4z"/></svg>`;
const GITHUB = `<svg viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M10 .5a9.700 9.700 0 0 0-3.100 18.900c.5.100.7-.200.700-.500v-1.700c-2.700.6-3.300-1.300-3.300-1.300-.4-1.100-1.100-1.400-1.100-1.400-.900-.6.100-.6.100-.6 1 .1 1.500 1 1.500 1 .9 1.500 2.300 1.100 2.900.8.100-.6.300-1.100.6-1.300-2.200-.200-4.400-1.100-4.400-4.800 0-1.100.4-1.900 1-2.600-.1-.300-.4-1.300.1-2.600 0 0 .800-.300 2.700 1a9.300 9.300 0 0 1 4.900 0c1.900-1.300 2.700-1 2.700-1 .5 1.300.2 2.300.1 2.600.6.700 1 1.600 1 2.600 0 3.700-2.300 4.600-4.400 4.800.3.300.6.900.6 1.800v2.700c0 .3.200.6.700.5A9.700 9.700 0 0 0 10 .5z"/></svg>`;

export function createSignIn(options: SignInOptions): HTMLElement {
  const { providers } = options;
  const note = h("p", { class: "signin-note", role: "alert" });
  const body = h("div", { class: "signin-body" });
  let busy = false;

  /** Run one step; a refusal is shown under the form and the form stays. */
  const attempt = async (step: () => Promise<void>) => {
    if (busy) return;
    busy = true;
    note.textContent = "";
    card.classList.add("busy");
    try {
      await step();
    } catch (error) {
      note.textContent = error instanceof Error ? error.message : t("signin.failed");
    } finally {
      busy = false;
      card.classList.remove("busy");
    }
  };

  // Text typed here is text: it must not reach the room's key handlers.
  const quiet = (input: HTMLInputElement) => {
    for (const type of ["keydown", "keyup"] as const) input.addEventListener(type, (event) => event.stopPropagation());
    return input;
  };

  const askEmail = (address = "") => {
    const email = quiet(h("input", {
      class: "field", type: "email", name: "email", autocomplete: "email", inputmode: "email", placeholder: t("signin.emailPlaceholder"),
      "aria-label": t("signin.email"),
    }));
    email.value = address;
    email.required = true;
    const form = h("form", { class: "signin-form" }, email, h("button", { class: "btn", type: "submit" }, t("signin.sendCode")));
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const to = email.value.trim();
      track("signin.method", { props: { method: "email" } });
      void attempt(async () => {
        try {
          await sendCode(to, lang());
        } catch (error) {
          // The address has had its three codes of these ten minutes, and the newest still works: the form
          // for it is shown, with the refusal under it. (A day's limit leaves no code that works.)
          if (error instanceof SignInRefusal && error.code === "CODE_LIMIT" && error.rule === "address") askCode(to, false);
          throw error;
        }
        askCode(to);
      });
    });
    const ways: HTMLElement[] = [];
    if (providers.google) {
      ways.push(h("button", { class: "btn ghost provider", type: "button", onclick: () => (track("signin.method", { props: { method: "google" } }), void attempt(() => signInWith("google"))) }, svg(GOOGLE), t("signin.google")));
    }
    if (providers.github) {
      ways.push(h("button", { class: "btn ghost provider", type: "button", onclick: () => (track("signin.method", { props: { method: "github" } }), void attempt(() => signInWith("github"))) }, svg(GITHUB), t("signin.github")));
    }
    if (providers.email) ways.push(form);
    if (options.onGuest && providers.guest) {
      // With no account system on this server, the guest way in is the only one, so it is the main button.
      ways.push(h("button", { class: ways.length ? "quiet" : "btn", type: "button", onclick: () => (track("signin.method", { props: { method: "guest" } }), void attempt(options.onGuest!)) }, t("signin.guest")));
    }
    body.replaceChildren(...ways);
  };

  /** The form for the code. `fresh` is false when no code was sent just now and an earlier one is asked for. */
  const askCode = (email: string, fresh = true) => {
    const code = quiet(h("input", {
      class: "field code", type: "text", name: "code", inputmode: "numeric", autocomplete: "one-time-code", maxlength: "6", placeholder: "000000",
      "aria-label": t("signin.code"),
    }));
    code.required = true;
    const form = h("form", { class: "signin-form" }, code, h("button", { class: "btn", type: "submit" }, t("signin.title")));
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void attempt(async () => {
        await signInWithCode(email, code.value.trim());
        options.onDone();
      });
    });
    body.replaceChildren(
      h("p", { class: "signin-sent", text: fresh ? t("signin.sent", { email }) : t("signin.newest", { email }) }),
      form,
      h("button", { class: "quiet", type: "button", onclick: () => askEmail(email) }, t("signin.otherAddress")));
    code.focus();
  };

  // Making an account is agreeing to both documents; the account records their versions at its first sign-in.
  const terms = h("p", { class: "signin-terms" }, ...rich("signin.terms", {
    terms: h("a", { href: localized(TERMS_PATH), target: "_blank", rel: "noopener" }, t("legal.terms")),
    privacy: h("a", { href: localized(PRIVACY_PATH), target: "_blank", rel: "noopener" }, t("legal.privacy")),
  }));
  const card = h("div", { class: "gate-card signin" }, h("h1", { text: options.title }), h("p", { text: options.lead }), body, note, terms);
  // Over the room before anyone is in, the gate is where a visitor changes the language.
  if (options.why === "gate") card.append(langSwitch());
  askEmail();
  track("signin.shown", { props: { why: options.why ?? "other" } });
  return card;
}

/** The language switch on the gate: each language by its own name; choosing one loads the room again in it. */
export function langSwitch(): HTMLElement {
  const now = lang();
  return h("p", { class: "account-lang", role: "group", "aria-label": t("lang.label") }, ...LANGS.map((code) =>
    h("button", {
      class: "quiet" + (code === now ? " on" : ""), type: "button", lang: LANG_INFO[code].tag, "aria-pressed": String(code === now),
      onclick: () => {
        if (code !== now) chooseLang(code);
      },
    }, LANG_INFO[code].name)));
}
